/**
 * WP7 — HouseBuildScene: tryb budowania na core/build (GridBoard, BlockPalette, LayoutSerializer).
 * Zapis: PUT /api/layout { locationType: 'home', layout, version } + mirror lokalny.
 */
import Phaser from 'phaser';
import {
  GRID_COLS,
  GRID_ROWS,
  TILE_PX,
  type PlacedBlock,
} from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { balanceBus } from '../../core/balanceBus.js';
import { GridBoard } from '../../core/build/GridBoard.js';
import { BlockPalette } from '../../core/build/BlockPalette.js';
import {
  serializeLayout,
  deserializeLayout,
} from '../../core/build/LayoutSerializer.js';
import { OutfitStore } from '../../avatar/outfitStore.js';
import {
  loadHouseLayout,
  loadOwnedBlocks,
  putHomeLayout,
  saveLocalHome,
} from './houseApi.js';
import {
  defaultLayerForBlock,
  homeBlockCatalog,
  normalizeLayout,
  placeBlock,
  remainingOwned,
  removeBlockAt,
} from './layoutModel.js';
import {
  blockColor,
  blockIconKind,
  blockLabelPl,
  blockTextureKey,
  type HomeBlockIconKind,
} from './blockVisuals.js';

export type HouseBuildSceneData = {
  houseId?: string;
  layout?: PlacedBlock[];
  layoutVersion?: number;
};

export class HouseBuildScene extends BaseScene {
  static readonly KEY = 'HouseBuild';

  private board = new GridBoard();
  private palette = new BlockPalette();
  private originX = 0;
  private originY = 0;
  private layout: PlacedBlock[] = [];
  private layoutVersion = 0;
  private houseId: string | null = null;
  private owned: string[] = [];
  private selectedId: string | null = null;
  private ghost: Phaser.GameObjects.Rectangle | null = null;
  private worldLayer: Phaser.GameObjects.Container | null = null;
  private paletteLayer: Phaser.GameObjects.Container | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private saving = false;
  private dirty = false;
  /** Unika postawienia w tym samym kliku co schowanie klocka. */
  private suppressPlace = false;
  /** Overlay sukcesu po zapisie — blokuje ponowne budowanie do „Dalej". */
  private successOpen = false;

  constructor() {
    super(HouseBuildScene.KEY);
  }

  init(data: HouseBuildSceneData): void {
    this.houseId = data?.houseId ?? null;
    if (data?.layout) {
      this.layout = normalizeLayout(data.layout);
    }
    if (typeof data?.layoutVersion === 'number') {
      this.layoutVersion = data.layoutVersion;
    }
  }

  preload(): void {
    this.preloadRealBg('bg_house');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0xffe4c4);
    this.addRealBg('bg_house');
    this.originX = (this.gameW - GRID_COLS * TILE_PX) / 2;
    this.originY = 48;

    this.owned = loadOwnedBlocks();
    this.palette.setOwned(this.owned);

    this.add
      .text(this.gameW / 2, 22, 'Budowanie domku', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#5d4037',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.statusText = this.add
      .text(this.gameW / 2, this.gameH - 18, 'Wybierz klocek, potem stuknij siatkę', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6d4c41',
      })
      .setOrigin(0.5);

    this.drawGrid();
    this.worldLayer = this.add.container(0, 0);
    this.paletteLayer = this.add.container(0, 0);

    this.ghost = this.add
      .rectangle(0, 0, TILE_PX - 4, TILE_PX - 4, 0xffffff, 0.35)
      .setStrokeStyle(2, 0x4caf50)
      .setVisible(false)
      .setDepth(50);

    this.paintBlocks();
    this.paintPalette();
    this.paintChrome();
    this.bindInput();

    void this.ensureLayoutLoaded();
  }

  private async ensureLayoutLoaded(): Promise<void> {
    if (this.layout.length > 0 || this.layoutVersion > 0) {
      // dane z HouseScene — i tak odśwież owned/local mirror
      saveLocalHome(this.layout, this.layoutVersion);
      this.paintBlocks();
      this.paintPalette();
      return;
    }
    const loaded = await loadHouseLayout({
      houseId: this.houseId ?? undefined,
    });
    if (!this.sys.isActive()) return;
    this.layout = loaded.layout;
    this.layoutVersion = loaded.layoutVersion;
    this.houseId = loaded.houseId;
    this.paintBlocks();
    this.paintPalette();
    this.setStatus(
      loaded.source === 'server'
        ? 'Układ z serwera — buduj i zapisz'
        : 'Układ lokalny — buduj i zapisz',
    );
  }

  private drawGrid(): void {
    const g = this.add.graphics();
    g.fillStyle(0xfff8e1, 1);
    g.fillRect(
      this.originX,
      this.originY,
      GRID_COLS * TILE_PX,
      GRID_ROWS * TILE_PX,
    );
    g.lineStyle(1, 0xffb74d, 0.55);
    for (let x = 0; x <= GRID_COLS; x++) {
      const wx = this.originX + x * TILE_PX;
      g.lineBetween(wx, this.originY, wx, this.originY + GRID_ROWS * TILE_PX);
    }
    for (let y = 0; y <= GRID_ROWS; y++) {
      const wy = this.originY + y * TILE_PX;
      g.lineBetween(this.originX, wy, this.originX + GRID_COLS * TILE_PX, wy);
    }
  }

  private tileCenter(tx: number, ty: number): { x: number; y: number } {
    const w = this.board.tileToWorld(tx, ty);
    return { x: this.originX + w.x, y: this.originY + w.y };
  }

  private pointerToTile(
    px: number,
    py: number,
  ): { tx: number; ty: number } | null {
    const localX = px - this.originX;
    const localY = py - this.originY;
    const { tx, ty } = this.board.worldToTile(localX, localY);
    if (!this.board.inBounds(tx, ty)) return null;
    return { tx, ty };
  }

  /** Proceduralne ikony klocków (bez emoji) — generateTexture. */
  private ensureBlockIconTexture(id: string): string {
    const key = blockTextureKey(id);
    if (this.textures.exists(key)) return key;
    const kind = blockIconKind(id);
    const g = this.make.graphics({ x: 0, y: 0 });
    drawHomeBlockIcon(g, kind, HOME_ICON_TEX, HOME_ICON_TEX);
    g.generateTexture(key, HOME_ICON_TEX, HOME_ICON_TEX);
    g.destroy();
    return key;
  }

  private paintBlocks(): void {
    this.worldLayer?.removeAll(true);
    const sorted = [...this.layout].sort((a, b) => a.layer - b.layer || a.y - b.y);
    for (const b of sorted) {
      const { x, y } = this.tileCenter(b.x, b.y);
      const rect = this.add
        .rectangle(x, y, TILE_PX - 6, TILE_PX - 6, blockColor(b.id))
        .setStrokeStyle(2, 0xffffff)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      rect.on('pointerdown', () => {
        // przenoszenie / chowanie: tap na postawiony = do pudełka
        this.suppressPlace = true;
        this.layout = removeBlockAt(this.layout, b.x, b.y, b.layer);
        this.dirty = true;
        this.selectedId = b.id;
        this.paintBlocks();
        this.paintPalette();
        this.setStatus(`Schowano: ${blockLabelPl(b.id)} (wrócił do pudełka)`);
      });
      const tex = this.ensureBlockIconTexture(b.id);
      const icon = this.add
        .image(x, y - 2, tex)
        .setDisplaySize(30, 30)
        .setOrigin(0.5);
      this.worldLayer?.add([rect, icon]);
    }
  }

  private paintPalette(): void {
    this.paletteLayer?.removeAll(true);
    const catalog = homeBlockCatalog().filter((b) => this.palette.has(b.id));
    const uniqueIds = [...new Set(catalog.map((c) => c.id))];
    // jeśli owned puste w sensie „nie ma w katalogu", pokaż te z qty remaining / domyślne
    const ids =
      uniqueIds.length > 0
        ? uniqueIds
        : homeBlockCatalog().map((c) => c.id);

    const itemW = Math.max(MIN_TOUCH + 8, 72);
    const gap = 8;
    const totalW = ids.length * (itemW + gap) - gap;
    let startX = (this.gameW - totalW) / 2 + itemW / 2;
    const y = this.originY + GRID_ROWS * TILE_PX + 48;

    // przewijanie jeśli za dużo — prosty clamp start
    if (totalW > this.gameW - 40) {
      startX = 20 + itemW / 2;
    }

    ids.forEach((id, i) => {
      const left = remainingOwned(this.owned, this.layout, id);
      const x = startX + i * (itemW + gap);
      const selected = this.selectedId === id;
      const bg = this.add
        .rectangle(x, y, itemW, MIN_TOUCH + 12, selected ? 0x80deea : 0xfff3e0)
        .setStrokeStyle(selected ? 4 : 2, selected ? 0x00838f : 0xffffff)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      const tex = this.ensureBlockIconTexture(id);
      const icon = this.add
        .image(x, y - 10, tex)
        .setDisplaySize(28, 28)
        .setOrigin(0.5);
      const qty = this.add
        .text(x, y + 18, `${left}×`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: left > 0 ? '#4a3f55' : '#b71c1c',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      bg.on('pointerdown', () => {
        this.selectedId = id;
        this.paintPalette();
        this.setStatus(`Wybrano: ${blockLabelPl(id)} — stuknij siatkę`);
      });
      this.paletteLayer?.add([bg, icon, qty]);
    });
  }

  private paintChrome(): void {
    UiKit.button(this, {
      x: 130,
      y: this.gameH - 70,
      label: 'Wróć do domku',
      fill: 0xffcc80,
      width: 200,
      onClick: () => this.goBack(),
    });
    UiKit.button(this, {
      x: this.gameW - 110,
      y: this.gameH - 70,
      label: 'Zapisz',
      fill: 0xa5d6a7,
      width: 160,
      onClick: () => {
        void this.save();
      },
    });
  }

  private bindInput(): void {
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.selectedId || !this.ghost) return;
      const tile = this.pointerToTile(p.x, p.y);
      if (!tile) {
        this.ghost.setVisible(false);
        return;
      }
      const { x, y } = this.tileCenter(tile.tx, tile.ty);
      this.ghost.setPosition(x, y);
      this.ghost.setFillStyle(blockColor(this.selectedId), 0.4);
      this.ghost.setVisible(true);
    });

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.suppressPlace) {
        this.suppressPlace = false;
        return;
      }
      if (!this.selectedId) return;
      // nie kładź gdy klik w UI chrome (dolny pasek)
      if (p.y > this.originY + GRID_ROWS * TILE_PX) return;
      const tile = this.pointerToTile(p.x, p.y);
      if (!tile) return;

      const layer = defaultLayerForBlock(this.selectedId);
      const result = placeBlock(
        this.layout,
        { id: this.selectedId, x: tile.tx, y: tile.ty, layer },
        this.owned,
      );
      if (!result.ok) {
        // jeśli zajęte — spróbuj schować istniejący i postawić (przenoszenie)
        if (result.reason === 'overlap') {
          this.layout = removeBlockAt(this.layout, tile.tx, tile.ty, layer);
          const retry = placeBlock(
            this.layout,
            { id: this.selectedId, x: tile.tx, y: tile.ty, layer },
            this.owned,
          );
          if (retry.ok) {
            this.layout = retry.layout;
            this.dirty = true;
            this.paintBlocks();
            this.paintPalette();
            this.setStatus(`Postawiono: ${blockLabelPl(this.selectedId!)}`);
            return;
          }
        }
        this.setStatus(this.reasonPl(result.reason));
        return;
      }
      this.layout = result.layout;
      this.dirty = true;
      this.paintBlocks();
      this.paintPalette();
      this.setStatus(`Postawiono: ${blockLabelPl(this.selectedId)}`);
    });
  }

  private reasonPl(reason: string): string {
    switch (reason) {
      case 'overlap':
        return 'Tu już coś stoi — stuknij klocek, by schować';
      case 'qty':
        return 'Koniec klocków tego typu — zbierz więcej w sklepie';
      case 'oob':
        return 'Poza siatką domku';
      case 'too_many':
        return 'Domek pełen — schowaj coś do pudełka';
      default:
        return 'Nie da się tu postawić';
    }
  }

  private setStatus(msg: string): void {
    this.statusText?.setText(msg);
  }

  private async save(): Promise<void> {
    if (this.saving || this.successOpen) return;
    this.saving = true;
    this.setStatus('Zapisuję domek…');

    // round-trip przez serializer WP0 (kontrakt silnika)
    const raw = serializeLayout(this.layout);
    const roundTrip = deserializeLayout(raw);
    this.layout = normalizeLayout(roundTrip);

    // zawsze mirror lokalny (offline-first)
    saveLocalHome(this.layout, this.layoutVersion);

    const remote = await putHomeLayout(this.layout, this.layoutVersion);
    if (!this.sys.isActive()) return;

    if (remote.ok) {
      this.layoutVersion = remote.version;
      this.dirty = false;
      saveLocalHome(this.layout, this.layoutVersion);
      if (remote.newBalanceZl !== null) {
        OutfitStore.saveBalance(remote.newBalanceZl);
        balanceBus.set(remote.newBalanceZl);
      }
      this.saving = false;
      this.showSaveSuccess({
        rewardZl: remote.rewardZl,
        offline: false,
      });
      return;
    }

    // sieć / konflikt — lokalny zapis i tak jest
    if (remote.code === 'VERSION_CONFLICT' || remote.status === 409) {
      this.setStatus('Ktoś zmienił domek — wczytuję świeży układ…');
      const fresh = await loadHouseLayout({ houseId: this.houseId ?? undefined });
      if (this.sys.isActive() && fresh) {
        this.layout = fresh.layout;
        this.layoutVersion = fresh.layoutVersion;
        this.paintBlocks();
        this.paintPalette();
        this.setStatus('Odświeżono układ (wersja się nie zgadzała)');
      }
      this.saving = false;
      return;
    }

    // offline / bez logowania / inny błąd — lokal OK, ekran sukcesu bez nagród serwera
    let localMsg = 'Zapisano domek u Ciebie!';
    if (remote.code === 'NETWORK' || remote.status === 0) {
      localMsg = 'Zapisano u Ciebie (offline) — wyślesz później';
      this.dirty = false;
    } else if (remote.code === 'UNAUTHORIZED' || remote.status === 401) {
      localMsg = 'Zapisano lokalnie (zaloguj się, by dostać nagrodę)';
      this.dirty = false;
    } else {
      localMsg = `Zapis lokalny OK · serwer: ${remote.code}`;
      this.dirty = false;
    }
    this.saving = false;
    this.showSaveSuccess({ rewardZl: 0, offline: true, detail: localMsg });
  }

  /**
   * Ekran sukcesu po zapisie: konfetti + komunikat PL + „Dalej" → House.
   * rewardZl > 0 → „Super domek! +N zł"; 0 online → nagroda już dziś / bez zmian.
   */
  private showSaveSuccess(opts: {
    rewardZl: number;
    offline: boolean;
    detail?: string;
  }): void {
    if (this.successOpen || !this.sys.isActive()) return;
    this.successOpen = true;

    const title =
      opts.rewardZl > 0
        ? `Super domek! +${opts.rewardZl} zł`
        : opts.offline
          ? 'Domek zapisany!'
          : 'Super domek!';
    const subtitle =
      opts.rewardZl > 0
        ? 'Dostałaś złotówki za budowanie ✨'
        : opts.offline
          ? (opts.detail ?? 'Układ jest u Ciebie na urządzeniu')
          : 'Nagroda za budowanie już dziś odebrana — wróć jutro po kolejne +10 zł';

    this.setStatus(title);

    const depth = 200;
    const cx = this.gameW / 2;
    const cy = this.gameH / 2;

    const dim = this.add
      .rectangle(cx, cy, this.gameW, this.gameH, 0x3e2723, 0.55)
      .setDepth(depth)
      .setInteractive(); // zjada pointer — nie buduj pod spodem

    const card = this.add
      .rectangle(cx, cy - 10, Math.min(420, this.gameW - 40), 220, 0xfff8e1, 0.98)
      .setStrokeStyle(4, 0xffb74d)
      .setDepth(depth + 1);

    const titleText = this.add
      .text(cx, cy - 70, title, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#5d4037',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: 360 },
      })
      .setOrigin(0.5)
      .setDepth(depth + 2);

    const subText = this.add
      .text(cx, cy - 20, subtitle, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6d4c41',
        align: 'center',
        wordWrap: { width: 360 },
      })
      .setOrigin(0.5)
      .setDepth(depth + 2);

    this.burstConfetti(depth + 3);

    const dalej = UiKit.button(this, {
      x: cx,
      y: cy + 70,
      label: 'Dalej',
      fill: 0xa5d6a7,
      width: 200,
      onClick: () => this.goBack(),
    });
    dalej.setDepth(depth + 4);
  }

  private burstConfetti(depth: number): void {
    const cx = this.gameW / 2;
    const cy = this.gameH / 2 - 20;
    const colors = [0xffb6c1, 0xb8e6d0, 0xffd700, 0xbde0fe, 0xffcc80, 0xce93d8];
    for (let i = 0; i < 28; i++) {
      const c = this.add
        .circle(cx, cy, 5 + Math.random() * 7, colors[i % colors.length]!)
        .setDepth(depth);
      const angle = Math.random() * Math.PI * 2;
      const dist = 70 + Math.random() * 180;
      this.tweens.add({
        targets: c,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        alpha: 0,
        duration: 800 + Math.random() * 500,
        ease: 'Cubic.easeOut',
        onComplete: () => c.destroy(),
      });
    }
  }

  private goBack(): void {
    if (this.dirty) {
      // auto-mirror draft
      saveLocalHome(this.layout, this.layoutVersion);
    }
    getSceneRouter().go('House', {
      houseId: this.houseId ?? undefined,
    });
  }
}

const HOME_ICON_TEX = 64;

function drawHomeBlockIcon(
  g: Phaser.GameObjects.Graphics,
  kind: HomeBlockIconKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;
  switch (kind) {
    case 'wall':
      g.fillStyle(0xf48fb1, 1);
      g.fillRoundedRect(cx - 22, cy - 20, 44, 40, 4);
      g.fillStyle(0xf8bbd0, 1);
      g.fillRect(cx - 18, cy - 8, 36, 4);
      g.fillRect(cx - 18, cy + 4, 36, 4);
      break;
    case 'floor':
      g.fillStyle(0xbcaaa4, 1);
      g.fillRoundedRect(cx - 22, cy - 18, 44, 36, 4);
      g.lineStyle(2, 0xffffff, 0.7);
      g.lineBetween(cx - 20, cy, cx + 20, cy);
      g.lineBetween(cx, cy - 16, cx, cy + 16);
      break;
    case 'sofa':
      g.fillStyle(0xff8a80, 1);
      g.fillRoundedRect(cx - 24, cy - 6, 48, 22, 6);
      g.fillRoundedRect(cx - 24, cy - 18, 14, 20, 6);
      g.fillRoundedRect(cx + 10, cy - 18, 14, 20, 6);
      break;
    case 'table':
      g.fillStyle(0xbcaaa4, 1);
      g.fillEllipse(cx, cy - 4, 44, 18);
      g.fillRect(cx - 4, cy + 2, 8, 16);
      g.fillEllipse(cx, cy + 18, 20, 8);
      break;
    case 'bed':
      g.fillStyle(0xf48fb1, 1);
      g.fillRoundedRect(cx - 22, cy - 4, 44, 20, 6);
      g.fillStyle(0xfce4ec, 1);
      g.fillRoundedRect(cx - 20, cy - 16, 18, 14, 4);
      break;
    case 'tv':
      g.fillStyle(0x78909c, 1);
      g.fillRoundedRect(cx - 20, cy - 14, 40, 26, 4);
      g.fillStyle(0x4fc3f7, 1);
      g.fillRect(cx - 14, cy - 8, 28, 14);
      g.fillStyle(0x90a4ae, 1);
      g.fillRect(cx - 3, cy + 12, 6, 8);
      break;
    case 'plant':
      g.fillStyle(0xa1887f, 1);
      g.fillRoundedRect(cx - 10, cy + 4, 20, 16, 4);
      g.fillStyle(0x81c784, 1);
      g.fillCircle(cx, cy - 8, 14);
      g.fillCircle(cx - 10, cy, 10);
      g.fillCircle(cx + 10, cy, 10);
      break;
    case 'roof':
      g.fillStyle(0xe57373, 1);
      g.fillTriangle(cx - 24, cy + 8, cx + 24, cy + 8, cx, cy - 18);
      break;
    case 'window':
      g.fillStyle(0xffe082, 1);
      g.fillRoundedRect(cx - 18, cy - 18, 36, 36, 4);
      g.fillStyle(0x81d4fa, 1);
      g.fillRect(cx - 14, cy - 14, 12, 12);
      g.fillRect(cx + 2, cy - 14, 12, 12);
      g.fillRect(cx - 14, cy + 2, 12, 12);
      g.fillRect(cx + 2, cy + 2, 12, 12);
      break;
    case 'box':
    default:
      g.fillStyle(0xb39ddb, 1);
      g.fillRoundedRect(cx - 18, cy - 18, 36, 36, 6);
      g.fillStyle(0xffffff, 0.4);
      g.fillRect(cx - 12, cy - 10, 24, 8);
      break;
  }
  g.lineStyle(2, 0xffffff, 0.85);
  g.strokeCircle(cx, cy, 28);
}

export default HouseBuildScene;
